/**
 * GuideView — LC 학습 가이드 컴포넌트 렌더
 *
 * - Mode 1 (simple message): 간단한 안내 메시지 + 확인 완료 버튼
 * - Mode 2 (audio response): 설명 퀴즈 + 1/2차 audio/text 응답 + 완료 버튼
 *
 * DESIGN.md 규범: queda 마스코트 · Soft Lift 카드 · Button3D Primary
 *
 * Spec: docs/superpowers/specs/2026-04-20-lc-learner-config-v2-ui-design.md §6.3
 */
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LearningContentText } from '@quebon/shared-learning/components/common/LearningContentText'
import { pickLocalizedText } from '@/utils/componentLabel'
import {
  completeSession,
  getAudioUploadUrl,
  saveAudioResponse,
  uploadAudioToS3,
  type LcGuideDataResponse,
} from '@/api/lcLearning'
import AudioRecorder from './AudioRecorder'
import { Button3D } from './ui/Button3D'
import { Card } from './ui/Card'
import { Mascot } from './ui/Mascot'
import type { RuntimeMode } from './ComponentRunner'

interface Props {
  componentProgressId: number | null
  enrollmentId: number
  componentType: string
  label: string
  guideData: LcGuideDataResponse | null
  runtimeMode?: RuntimeMode
  onComplete: () => void
}

export default function GuideView({
  componentProgressId,
  enrollmentId,
  componentType,
  label,
  guideData,
  runtimeMode = 'live',
  onComplete,
}: Props) {
  const { t, i18n } = useTranslation('learner')
  const isLive = runtimeMode === 'live'
  const [completing, setCompleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [slot1Submitted, setSlot1Submitted] = useState(!!guideData?.audioResponse1)
  const [slot1Skipped, setSlot1Skipped] = useState(false)
  const [slot2Submitted, setSlot2Submitted] = useState(!!guideData?.audioResponse2)

  const guideMessage = pickLocalizedText(guideData?.message, guideData?.messageEn, i18n.language)
  const hasAudioResponse = guideData?.explainQuiz != null
  const canComplete = !isLive || !hasAudioResponse || slot1Submitted || slot1Skipped

  const handleAudioSubmit = useCallback(
    async (slot: 1 | 2, blob: Blob) => {
      if (!isLive || componentProgressId == null) return
      const { uploadUrl, fileUrl } = await getAudioUploadUrl(componentProgressId, slot)
      await uploadAudioToS3(uploadUrl, blob)
      await saveAudioResponse(componentProgressId, slot, fileUrl)
      if (slot === 1) setSlot1Submitted(true)
      if (slot === 2) setSlot2Submitted(true)
    },
    [isLive, componentProgressId],
  )

  async function handleComplete() {
    if (!canComplete || completing) return
    setCompleting(true)
    setError(null)
    try {
      if (isLive && componentProgressId != null) {
        await completeSession(componentProgressId, {
          enrollmentId,
          componentType,
        })
      }
      onComplete()
    } catch {
      setError('완료 처리 중 오류가 발생했습니다.')
      setCompleting(false)
    }
  }

  // TODO(option): honor GuideOptions.audioResponse.enabled — currently
  // derived from guideData.explainQuiz presence; option-driven toggle
  // deferred to follow-up spec "LC Learning Config v2 Option Logic".

  // === Mode 1: Simple message ===
  if (!hasAudioResponse) {
    return (
      <div className="flex flex-col items-center gap-6 max-w-lg mx-auto">
        <Card size="lg" className="w-full flex items-start gap-4">
          <Mascot name="queda" shape="roundbox" size={80} className="shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="text-[24px] leading-[1.5] tracking-[-0.6px] font-medium text-lc-gray-900 mb-2">
              {label}
            </h2>
            <p className="text-[18px] leading-[1.5] tracking-[-0.6px] font-normal text-lc-gray-700 whitespace-pre-wrap">
              {guideMessage ?? '안내 내용을 확인한 후 완료 버튼을 눌러주세요.'}
            </p>
          </div>
        </Card>

        {error && <ErrorCard message={error} />}

        <Button3D
          variant="orange"
          size="L"
          onClick={handleComplete}
          disabled={completing || !canComplete}
        >
          {completing ? '처리 중...' : '확인 완료'}
        </Button3D>
      </div>
    )
  }

  // === Mode 2: Audio Response ===
  const quiz = guideData.explainQuiz!

  return (
    <div className="flex flex-col gap-5 max-w-lg mx-auto">
      {guideMessage && (
        <Card size="md" className="flex items-start gap-3 bg-lc-lightblue-100">
          <Mascot name="queda" shape="roundbox" size={48} className="shrink-0" />
          <p className="flex-1 text-[18px] leading-[1.5] tracking-[-0.6px] font-normal text-lc-gray-700 whitespace-pre-wrap">
            {guideMessage}
          </p>
        </Card>
      )}

      <Card size="md">
        {quiz.title && (
          <h3 className="text-[20px] leading-[1.5] tracking-[-0.6px] font-medium text-lc-gray-900 mb-3">
            <LearningContentText text={quiz.title} />
          </h3>
        )}
        {quiz.image && (
          <img
            src={quiz.image}
            alt="문제 이미지"
            className="w-full rounded-lc-md"
          />
        )}
      </Card>

      {!isLive ? (
        <Card size="md" className="flex flex-col gap-2">
          <p className="text-[16px] leading-[1.5] tracking-[-0.6px] text-lc-gray-600">
            {t('library.guideReadOnly')}
          </p>
        </Card>
      ) : !slot1Submitted && !slot1Skipped ? (
        <Card size="md" className="flex flex-col gap-3">
          <h4 className="text-[16px] font-medium tracking-[-0.6px] text-lc-gray-700">
            1차 응답
          </h4>
          <AudioRecorder
            label="1차 녹음"
            onSubmit={(blob) => handleAudioSubmit(1, blob)}
            existingUrl={guideData.audioResponse1}
            unavailableActionLabel={t('guideView.skipRecording')}
            onUnavailableAction={() => setSlot1Skipped(true)}
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          <Card size="md">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-lc-mint-900 text-white flex items-center justify-center text-[14px] font-bold">
                ✓
              </div>
              <span className="text-[16px] font-medium tracking-[-0.6px] text-lc-mint-dark">
                {slot1Skipped ? t('guideView.firstResponseSkipped') : '1차 응답 제출 완료'}
              </span>
            </div>
            {guideData.audioResponse1?.startsWith('https://') && (
              <audio
                src={guideData.audioResponse1}
                controls
                className="w-full h-10"
              />
            )}
            {guideData.audioResponse1 && !guideData.audioResponse1.startsWith('https://') && (
              <p className="text-[16px] font-normal tracking-[-0.6px] text-lc-gray-600 bg-lc-gray-100 rounded-lc-md p-3 mt-2">
                {guideData.audioResponse1}
              </p>
            )}
          </Card>

          <Card size="md" className="flex items-center gap-3 bg-lc-orange-100">
            <Mascot name="queda" shape="roundbox" size={48} className="shrink-0" />
            <p className="flex-1 text-[16px] font-medium tracking-[-0.6px] text-lc-gray-800">
              선생님께 가서 들려주세요
            </p>
          </Card>

          {!slot2Submitted && (
            <Card size="md" className="flex flex-col gap-2">
              <h4 className="text-[16px] font-medium tracking-[-0.6px] text-lc-gray-700">
                2차 녹음 (선택)
              </h4>
              <AudioRecorder
                label="2차 녹음"
                onSubmit={(blob) => handleAudioSubmit(2, blob)}
                existingUrl={guideData.audioResponse2}
              />
            </Card>
          )}

          {slot2Submitted && (
            <Card size="md">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-lc-mint-900 text-white flex items-center justify-center text-[14px] font-bold">
                  ✓
                </div>
                <span className="text-[16px] font-medium tracking-[-0.6px] text-lc-mint-dark">
                  2차 응답 제출 완료
                </span>
              </div>
            </Card>
          )}
        </div>
      )}

      {error && <ErrorCard message={error} />}

      <div className="flex justify-center pt-2">
        <Button3D
          variant="orange"
          size="L"
          onClick={handleComplete}
          disabled={completing || !canComplete}
        >
          {completing ? '처리 중...' : '확인 완료'}
        </Button3D>
      </div>
    </div>
  )
}

function ErrorCard({ message }: { message: string }) {
  return (
    <Card size="md" className="flex items-center gap-3">
      <div
        aria-label="error"
        className="w-8 h-8 rounded-full bg-lc-red-900 text-white flex items-center justify-center text-[18px] font-bold shrink-0"
      >
        ✕
      </div>
      <p className="text-[16px] leading-[1.5] tracking-[-0.6px] text-lc-red-dark">
        {message}
      </p>
    </Card>
  )
}
